import { useI18n } from "@/contexts/I18nContext";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function PrivacyPolicyPage() {
  const { language } = useI18n();
  const isAr = language === "ar";

  const en = {
    title: "Privacy Policy",
    effective: "Effective: August 2026",
    intro:
      "Souq Crypto operates as an independent business directory that connects buyers with USDT-friendly merchants in Dubai. This policy explains what personal data we collect, why, how it is used, and your rights.",
    sections: [
      {
        heading: "1. What we collect",
        body: "When you submit a \"Request This Deal\" form on a listing page, we collect: your name, WhatsApp number, and optionally your email address. You must actively consent before any data is stored. We do not collect wallet addresses, payment details, or government IDs.",
      },
      {
        heading: "2. Why we collect it",
        body: "We collect contact details to: (a) generate a unique reference code for your enquiry, (b) connect you with the relevant merchant, and (c) internally track which listings generate leads so we can improve the directory.",
      },
      {
        heading: "3. Who receives your data",
        body: "Your name and WhatsApp number are shared with the specific merchant whose listing you requested a deal from. No other third party receives your personal data. We do not sell, rent, or trade personal information.",
      },
      {
        heading: "4. How we store it",
        body: "Lead data is stored in a secured database hosted in the EU. Access is restricted to authorised Souq Crypto administrators. Data is retained for up to 12 months after collection unless you request earlier deletion.",
      },
      {
        heading: "5. No financial data",
        body: "Souq Crypto does not process payments, hold cryptocurrency, or facilitate fund transfers. All payment arrangements are made directly between buyer and merchant. We never collect wallet addresses, transaction hashes, or banking information.",
      },
      {
        heading: "6. Analytics, session recording, and error monitoring",
        body: "We use three tools to understand and improve the website:\n\n• Google Analytics 4 (GA4) — tracks page views, clicks, and conversions (e.g. WhatsApp clicks, enquiries submitted). IP addresses are anonymised. GA4 only loads after you accept the cookie-consent banner. You can change or withdraw your consent at any time via the \"Cookie Preferences\" link in the website footer; your choice is stored in your browser's local storage, and declining immediately stops all analytics tracking.\n\n• Microsoft Clarity — records anonymised session replays and heatmaps to help us understand how visitors navigate the site. All text inputs (including search fields and forms) are automatically masked so their contents are never recorded. Clarity only loads after you accept the cookie-consent banner.\n\n• Sentry — captures JavaScript errors so we can fix bugs. Sentry does not use cookies and does not collect personal data. Before any error report is sent, it is automatically scrubbed to remove phone numbers, email addresses, wallet addresses, and authentication tokens. Sentry is loaded regardless of your cookie-consent choice because it operates under a legitimate-interest basis with no personal data collected.\n\nNo advertising or cross-site tracking cookies are used.",
      },
      {
        heading: "7. Your rights",
        body: "You have the right to: (a) request access to the personal data we hold about you, (b) request correction or deletion of your data, (c) withdraw consent at any time. To exercise these rights, contact us at privacy@souqcrypto.ae.",
      },
      {
        heading: "8. Updates to this policy",
        body: "We may update this policy when our practices change. Material changes will be noted with an updated effective date at the top of this page.",
      },
    ],
    contact: "Questions? Contact us at privacy@souqcrypto.ae",
    back: "Back to Home",
  };

  const ar = {
    title: "سياسة الخصوصية",
    effective: "السريان: أغسطس 2026",
    intro:
      "يعمل سوق كريبتو بوصفه دليلاً تجارياً مستقلاً يربط المشترين بالتجار المتعاملين بـ USDT في دبي. توضح هذه السياسة البيانات الشخصية التي نجمعها، والسبب، وكيفية استخدامها، وحقوقك.",
    sections: [
      {
        heading: "1. ما الذي نجمعه",
        body: 'عند تقديم نموذج "طلب هذه الصفقة" على صفحة الإعلان، نجمع: اسمك، ورقم واتساب الخاص بك، وبريدك الإلكتروني اختيارياً. يجب أن توافق صراحةً قبل تخزين أي بيانات. لا نجمع عناوين المحافظ أو تفاصيل الدفع أو الهويات الرسمية.',
      },
      {
        heading: "2. لماذا نجمعها",
        body: "نجمع بيانات التواصل لـ: (أ) إنشاء رمز مرجعي فريد لاستفسارك، (ب) ربطك بالتاجر المعني، (ج) متابعة الإعلانات التي تولّد عملاء محتملين داخلياً لتحسين الدليل.",
      },
      {
        heading: "3. من يتلقى بياناتك",
        body: "يتم مشاركة اسمك ورقم واتساب مع التاجر المحدد الذي طلبت صفقته. لا تتلقى أي جهة خارجية أخرى بياناتك الشخصية. لا نبيع أو نؤجر أو نتاجر بالمعلومات الشخصية.",
      },
      {
        heading: "4. كيف نحفظها",
        body: "تُخزَّن بيانات العملاء في قاعدة بيانات آمنة مستضافة في الاتحاد الأوروبي. الوصول مقيد بمسؤولي سوق كريبتو المخولين. تُحتفظ بالبيانات لمدة 12 شهراً من تاريخ الجمع ما لم تطلب الحذف المبكر.",
      },
      {
        heading: "5. لا بيانات مالية",
        body: "لا يعالج سوق كريبتو المدفوعات، ولا يحتفظ بعملات مشفرة، ولا يسهّل تحويل الأموال. تتم جميع ترتيبات الدفع مباشرةً بين المشتري والتاجر. لا نجمع عناوين المحافظ أو تجزئات المعاملات أو المعلومات المصرفية أبداً.",
      },
      {
        heading: "6. التحليلات وتسجيل الجلسات ومراقبة الأخطاء",
        body: "نستخدم ثلاث أدوات لتحسين الموقع:\n\n• Google Analytics 4 (GA4) — يتتبع مشاهدات الصفحات والنقرات والتحويلات (مثل نقرات واتساب والاستفسارات). تُخفى عناوين IP. لا يُحمَّل GA4 إلا بعد قبولك لبانر الموافقة. يمكنك تغيير أو سحب موافقتك في أي وقت عبر رابط \"تفضيلات ملفات الارتباط\" في أسفل الموقع؛ يُخزَّن خيارك في التخزين المحلي لمتصفحك، ويؤدي الرفض إلى إيقاف التتبع فوراً.\n\n• Microsoft Clarity — يسجل جلسات مجهولة الهوية وخرائط حرارية لفهم كيفية تصفح الزوار للموقع. جميع حقول الإدخال مُقنَّعة تلقائياً ولا تُسجَّل أي نصوص مكتوبة. لا يُحمَّل إلا بعد قبول الموافقة.\n\n• Sentry — يرصد أخطاء JavaScript لتمكيننا من إصلاحها. لا يستخدم Sentry ملفات ارتباط ولا يجمع بيانات شخصية. تُحذف أرقام الهواتف وعناوين البريد الإلكتروني وعناوين المحافظ ورموز المصادقة تلقائياً من أي تقرير خطأ قبل إرساله. يعمل Sentry بغض النظر عن خيار الموافقة وفق أساس المصلحة المشروعة.\n\nلا تُستخدم ملفات تعريف ارتباط إعلانية أو تتبع عبر المواقع.",
      },
      {
        heading: "7. حقوقك",
        body: "يحق لك: (أ) طلب الاطلاع على البيانات الشخصية التي نحتفظ بها عنك، (ب) طلب تصحيح بياناتك أو حذفها، (ج) سحب موافقتك في أي وقت. للممارسة هذه الحقوق، راسلنا على privacy@souqcrypto.ae.",
      },
      {
        heading: "8. تحديثات هذه السياسة",
        body: "قد نحدّث هذه السياسة عند تغيير ممارساتنا. ستُشار التغييرات الجوهرية بتاريخ سريان محدّث في أعلى هذه الصفحة.",
      },
    ],
    contact: "هل لديك أسئلة؟ تواصل معنا على privacy@souqcrypto.ae",
    back: "العودة إلى الرئيسية",
  };

  const content = isAr ? ar : en;

  return (
    <div className="flex-1 bg-background py-12 px-4">
      <div className="container mx-auto max-w-3xl">
        <Button variant="ghost" asChild className="mb-6 -ml-2 text-muted-foreground hover:text-foreground">
          <Link href="/">
            <ArrowLeft className="mr-2 h-4 w-4" /> {content.back}
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-8 w-8 text-primary shrink-0" />
          <h1 className="text-3xl font-extrabold tracking-tight">{content.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">{content.effective}</p>

        <p className="text-base text-foreground/80 mb-8 leading-relaxed">{content.intro}</p>

        <div className="space-y-8">
          {content.sections.map((s) => (
            <div key={s.heading}>
              <h2 className="text-lg font-bold mb-2">{s.heading}</h2>
              <p className="text-foreground/80 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-muted rounded-xl p-6 text-sm text-muted-foreground">
          {content.contact}
        </div>
      </div>
    </div>
  );
}
